// src/pages/TimeOffManagement.js
import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, TextField, Grid, Card, CardContent,
  IconButton, Tooltip, Avatar
} from '@mui/material';
import {
  Add, CheckCircle, Cancel, Pending, Warning,
  BeachAccess, Sick, CalendarToday
} from '@mui/icons-material';
import { getData, saveData } from '../services/storageService';
import { useI18n } from '../i18n/i18n';
import { useNotifications } from '../context/NotificationContext';

const TimeOffManagement = () => {
  const { t } = useI18n();
  const { showNotification } = useNotifications();
  
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [staff, setStaff] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const staffData = await getData('staff') || [];
      const timeOffData = await getData('timeoff_requests') || [];
      
      setStaff(staffData.filter(s => s.status === 'active'));
      setTimeOffRequests(timeOffData);
    } catch (error) {
      console.error("Error loading data:", error);
      showNotification(t('timeoff.load_error'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRequest = async (requestData) => {
    try {
      const newRequest = {
        id: Date.now().toString(),
        ...requestData,
        status: 'pending',
        submittedAt: new Date().toISOString()
      };

      const updatedRequests = [...timeOffRequests, newRequest];
      await saveData('timeoff_requests', updatedRequests);
      setTimeOffRequests(updatedRequests);
      
      showNotification(t('timeoff.request_submitted'), 'success');
      setDialogOpen(false);
    } catch (error) {
      showNotification(t('timeoff.submit_error'), 'error');
    }
  };

  const handleApproveRequest = async (requestId) => {
    try {
      const updatedRequests = timeOffRequests.map(request =>
        request.id === requestId
          ? { 
              ...request, 
              status: 'approved',
              approvedAt: new Date().toISOString(),
              approvedBy: 'Supervisor' // В реальній системі - поточний користувач
            }
          : request
      );

      await saveData('timeoff_requests', updatedRequests);
      setTimeOffRequests(updatedRequests);
      
      showNotification(t('timeoff.request_approved'), 'success');
    } catch (error) {
      showNotification(t('timeoff.approve_error'), 'error');
    }
  };

  const handleRejectRequest = async (requestId, reason) => {
    try {
      const updatedRequests = timeOffRequests.map(request =>
        request.id === requestId
          ? { 
              ...request, 
              status: 'rejected',
              rejectionReason: reason,
              approvedAt: new Date().toISOString(),
              approvedBy: 'Supervisor'
            }
          : request
      );

      await saveData('timeoff_requests', updatedRequests);
      setTimeOffRequests(updatedRequests);
      
      showNotification(t('timeoff.request_rejected'), 'warning');
    } catch (error) {
      showNotification(t('timeoff.reject_error'), 'error');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle />;
      case 'rejected': return <Cancel />;
      case 'pending': return <Pending />;
      default: return <Warning />;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'vacation': return <BeachAccess />;
      case 'sick': return <Sick />;
      case 'personal': return <CalendarToday />;
      default: return <CalendarToday />;
    }
  };

  const stats = {
    total: timeOffRequests.length,
    pending: timeOffRequests.filter(r => r.status === 'pending').length,
    approved: timeOffRequests.filter(r => r.status === 'approved').length,
    rejected: timeOffRequests.filter(r => r.status === 'rejected').length,
    upcoming: timeOffRequests.filter(r => 
      r.status === 'approved' && new Date(r.startDate) > new Date()
    ).length
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
        {t('timeoff.title')}
      </Typography>

      {/* Статистика */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          { value: stats.total, label: t('timeoff.total_requests'), color: 'primary' },
          { value: stats.pending, label: t('timeoff.pending'), color: 'warning' },
          { value: stats.approved, label: t('timeoff.approved'), color: 'success' },
          { value: stats.upcoming, label: t('timeoff.upcoming'), color: 'info' }
        ].map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ textAlign: 'center', p: 3, borderRadius: 3 }}>
              <Typography variant="h3" sx={{ fontWeight: 700, color: `${stat.color}.main` }}>
                {stat.value}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {stat.label}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Панель керування */}
      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {t('timeoff.requests')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setDialogOpen(true)}
            >
              {t('timeoff.new_request')}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Таблиця запитів */}
      <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('timeoff.employee')}</TableCell>
              <TableCell>{t('timeoff.type')}</TableCell>
              <TableCell>{t('timeoff.period')}</TableCell>
              <TableCell>{t('timeoff.days')}</TableCell>
              <TableCell>{t('timeoff.status')}</TableCell>
              <TableCell>{t('timeoff.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {timeOffRequests.map((request) => {
              const employee = staff.find(s => s.id === request.employeeId);
              if (!employee) return null;

              const startDate = new Date(request.startDate);
              const endDate = new Date(request.endDate);
              const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

              return (
                <TableRow key={request.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ width: 40, height: 40 }}>
                        {employee.name?.charAt(0) || employee.firstName?.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography fontWeight="500">
                          {employee.name || `${employee.firstName} ${employee.lastName}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {employee.department}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getTypeIcon(request.type)}
                      label={t(`timeoff.type_${request.type}`)}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {startDate.toLocaleDateString()} - {endDate.toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight="500">
                      {days} {t('timeoff.days')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(request.status)}
                      label={t(`timeoff.status_${request.status}`)}
                      color={getStatusColor(request.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {request.status === 'pending' && (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title={t('timeoff.approve')}>
                          <IconButton
                            color="success"
                            onClick={() => handleApproveRequest(request.id)}
                            size="small"
                          >
                            <CheckCircle />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('timeoff.reject')}>
                          <IconButton
                            color="error"
                            onClick={() => {
                              const reason = prompt(t('timeoff.rejection_reason_prompt'));
                              if (reason) handleRejectRequest(request.id, reason);
                            }}
                            size="small"
                          >
                            <Cancel />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {timeOffRequests.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CalendarToday sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              {t('timeoff.no_requests')}
            </Typography>
            <Button 
              variant="outlined" 
              onClick={() => setDialogOpen(true)}
              sx={{ mt: 2 }}
            >
              {t('timeoff.create_first_request')}
            </Button>
          </Box>
        )}
      </TableContainer>

      {/* Діалог створення запиту */}
      <TimeOffRequestDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmitRequest}
        staff={staff}
        t={t}
      />
    </Box>
  );
};

const TimeOffRequestDialog = ({ open, onClose, onSubmit, staff, t }) => {
  const [formData, setFormData] = useState({
    employeeId: '',
    type: 'vacation',
    startDate: '',
    endDate: '',
    reason: ''
  });

  const handleSubmit = () => {
    if (!formData.employeeId || !formData.startDate || !formData.endDate) {
      alert(t('timeoff.fill_required_fields'));
      return;
    }

    onSubmit(formData);
    setFormData({
      employeeId: '',
      type: 'vacation',
      startDate: '',
      endDate: '',
      reason: ''
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('timeoff.new_request')}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <FormControl fullWidth>
            <InputLabel>{t('timeoff.employee')}</InputLabel>
            <Select
              value={formData.employeeId}
              label={t('timeoff.employee')}
              onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
            >
              {staff.map(employee => (
                <MenuItem key={employee.id} value={employee.id}>
                  {employee.name || `${employee.firstName} ${employee.lastName}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>{t('timeoff.type')}</InputLabel>
            <Select
              value={formData.type}
              label={t('timeoff.type')}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            >
              <MenuItem value="vacation">{t('timeoff.type_vacation')}</MenuItem>
              <MenuItem value="sick">{t('timeoff.type_sick')}</MenuItem>
              <MenuItem value="personal">{t('timeoff.type_personal')}</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label={t('timeoff.start_date')}
            type="date"
            value={formData.startDate}
            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            fullWidth
            label={t('timeoff.end_date')}
            type="date"
            value={formData.endDate}
            onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            fullWidth
            label={t('timeoff.reason')}
            multiline
            rows={3}
            value={formData.reason}
            onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
            placeholder={t('timeoff.reason_placeholder')}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button onClick={handleSubmit} variant="contained">
          {t('timeoff.submit_request')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TimeOffManagement;